import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Row, Col, Avatar, Typography, Empty, Badge, Tooltip } from 'antd';
import { UserAddOutlined, TrophyOutlined, StarFilled, CrownOutlined } from '@ant-design/icons';
import { SERVER_IP } from 'assets/Config';
import { useSelector } from 'react-redux';
import { fetchCustomerImage } from 'services/customerImages';
import MaleSVG from '../../../assets/images/male.svg';
import FemaleSVG from '../../../assets/images/female.svg';
import './top-referrers.scss';

const { Text, Title } = Typography;

const TopReferrers = ({ referrers = [], loading = false }) => {
	// Validate and filter referrers
	const validReferrers = useMemo(() => {
		if (!referrers || !Array.isArray(referrers)) return [];
		return referrers.filter(ref => ref && (ref.customerName || ref.id));
	}, [referrers]);

	if (loading) {
		return (
			<Row className="top-referrers-card" gutter={[0, 16]}>
				<Col span={24}>
					<div className="referrer-skeleton">
						<div className="skeleton-avatar"></div>
						<div className="skeleton-content">
							<div className="skeleton-line"></div>
							<div className="skeleton-line short"></div>
						</div>
					</div>
				</Col>
			</Row>
		);
	}

	if (!validReferrers || validReferrers.length === 0) {
		return (
			<Row className="top-referrers-card">
				<Col span={24}>
					<div className="referrers-header">
						<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
							<CrownOutlined style={{ fontSize: '14px', color: '#ffd700' }} />
							<span>Top Referrers</span>
						</div>
					</div>
					<div className="empty-state">
						<Empty 
							description={<span className="empty-text">No referral data available</span>} 
							image={Empty.PRESENTED_IMAGE_SIMPLE}
							imageStyle={{ opacity: 0.5 }}
						/>
					</div>
				</Col>
			</Row>
		);
	}

	return (
		<Row className="top-referrers-card">
			<Col span={24}>
				<div className="referrers-header">
					<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
						<CrownOutlined style={{ fontSize: '14px', color: '#ffd700' }} />
						<span>Top Referrers</span>
					</div>
				</div>
				<Row gutter={[0, 12]} className="referrers-list">
					{validReferrers.map((referrer, index) => (
						<ReferrerItem key={referrer.id || index} referrer={referrer} rank={index + 1} />
					))}
				</Row>
			</Col>
		</Row>
	);
};

const ReferrerItem = ({ referrer, rank }) => {
	const { customerName, referralCount, gender, image, mobile } = referrer;
	const loginRedux = useSelector((state) => state.loginRedux);
	const [imageUrl, setImageUrl] = useState(null);
	const imageUrlRef = useRef(null);

	const clearImageUrl = useCallback((updateState = true) => {
		if (imageUrlRef.current) {
			URL.revokeObjectURL(imageUrlRef.current);
			imageUrlRef.current = null;
		}
		if (updateState) {
			setImageUrl(null);
		}
	}, []);

	useEffect(() => {
		let isMounted = true;

		const loadImage = async () => {
			clearImageUrl();
			if (!referrer?.id || !loginRedux?.token) return;

			const result = await fetchCustomerImage(referrer.id, loginRedux.token);

			if (!isMounted) {
				if (result?.data) {
					URL.revokeObjectURL(result.data);
				}
				return;
			}

			if (result?.data) {
				imageUrlRef.current = result.data;
				setImageUrl(result.data);
			} else if (result?.error) {
				console.error('Failed to load referrer image', result.error);
			}
		};

		loadImage();

		return () => {
			isMounted = false;
			clearImageUrl(false);
		};
	}, [clearImageUrl, loginRedux?.token, referrer?.id]);
	
	const getRankBadge = () => {
		if (rank === 1) return { icon: '🥇', color: '#ffd700', className: 'gold' };
		if (rank === 2) return { icon: '🥈', color: '#c0c0c0', className: 'silver' };
		if (rank === 3) return { icon: '🥉', color: '#cd7f32', className: 'bronze' };
		return { icon: `#${rank}`, color: '#90a4ae', className: 'default' };
	};

	const rankBadge = getRankBadge();
	const defaultAvatar = gender === 'female' ? FemaleSVG : MaleSVG;
	const existingImage = image?.[0]?.url ? SERVER_IP + image[0].url : null;
	const avatarSrc = imageUrl || existingImage || defaultAvatar; // Default to male avatar if gender is not provided

	return (
		<Col span={24}>
			<div className={`referrer-item ${rankBadge.className}`}>
				<div className="referrer-rank">
					<span className="rank-badge" style={{ color: rankBadge.color }}>
						{rankBadge.icon}
					</span>
				</div>
				<div className="referrer-avatar-wrapper">
					<Avatar
						src={avatarSrc}
						size={48}
						icon={<UserAddOutlined />}
						className="referrer-avatar"
					/>
					{rank <= 3 && <div className="rank-glow"></div>}
				</div>
				<div className="referrer-content">
					<div className="referrer-name-row">
						<Text strong className="referrer-name">
							{customerName || 'Unknown'}
						</Text>
						{rank <= 3 && <StarFilled className="star-badge" />}
					</div>
					<div className="referrer-stats">
						<UserAddOutlined className="referral-icon" />
						<Text className="referral-count">
							{referralCount || 0} {referralCount === 1 ? 'Referral' : 'Referrals'}
						</Text>
					</div>
				</div>
				<div className="referrer-badge-wrapper">
					<Badge
						count={referralCount || 0}
						overflowCount={99}
						className="referral-badge"
						style={{
							backgroundColor: rankBadge.color,
							boxShadow: `0 0 10px ${rankBadge.color}40`,
						}}
					/>
				</div>
			</div>
		</Col>
	);
};

export default TopReferrers;
